// src/components/Dashboard/SLAUserSummaryCards.tsx
import React from "react";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  TrophyOutlined,
  WarningOutlined,
} from "@ant-design/icons";

interface User {
  user_id: string;
  user_name: string;
  user_email: string;
  team?: string;
}

interface SLAUserSummaryCardsProps {
  userData: any;
  userInfo: User | null;
}

const SLAUserSummaryCards: React.FC<SLAUserSummaryCardsProps> = ({
  userData,
  userInfo,
}) => {
  if (
    !userData ||
    !userData.combined_compliance ||
    userData.combined_compliance.length === 0
  ) {
    return null;
  }

  const user = userData.combined_compliance[0];

  // Calculate metrics from user data
  const overallCompliance = user.compliance_pct;
  const beforeCutoffCompliance = user.before_cutoff_pct;
  const afterCutoffCompliance = user.after_cutoff_pct;
  const totalProcessed = user.total_processed;
  const totalCompliant = user.compliant;
  const avgVerificationTimeBusinessHours = user.avg_time_hours;

  // Performance evaluation
  const getPerformanceLevel = (percentage: number) => {
    if (percentage >= 95)
      return { level: "Excelente", color: "bg-green-100 text-green-600" };
    if (percentage >= 85)
      return { level: "Bueno", color: "bg-blue-100 text-blue-600" };
    if (percentage >= 75)
      return { level: "Regular", color: "bg-yellow-100 text-yellow-600" };
    return { level: "Necesita Mejora", color: "bg-red-100 text-red-600" };
  };

  // Color coding functions
  const getComplianceColor = (percentage: number) => {
    if (percentage >= 95) return "bg-green-100 text-green-600";
    if (percentage >= 85) return "bg-blue-100 text-blue-600";
    if (percentage >= 75) return "bg-yellow-100 text-yellow-600";
    return "bg-red-100 text-red-600";
  };

  const performance = getPerformanceLevel(overallCompliance);

  // Check if meeting targets
  const meetingBeforeTarget = beforeCutoffCompliance >= 85;
  const meetingAfterTarget = afterCutoffCompliance >= 90;

  // Card data configuration
  const cardItems = [
    {
      title: "Cumplimiento General",
      value: `${overallCompliance.toFixed(1)}%`,
      icon: <CheckCircleOutlined className="text-2xl" />,
      color: getComplianceColor(overallCompliance),
      subtitle: `${totalCompliant.toLocaleString()}/${totalProcessed.toLocaleString()} requisitos`,
      badge: performance.level,
      badgeColor: performance.color,
    },
    {
      title: "Primer bloque",
      value: `${beforeCutoffCompliance.toFixed(1)}%`,
      icon: <ClockCircleOutlined className="text-2xl" />,
      // color: getComplianceColor(beforeCutoffCompliance),
      color: "bg-gray-100 text-gray-600",
      // subtitle: `Meta: 85%`,
      subtitle: null,
      // badge: meetingBeforeTarget ? "✓ Cumple" : "✗ No Cumple",
      badge: null,
      // badgeColor: meetingBeforeTarget
      //   ? "bg-green-100 text-green-800"
      //   : "bg-red-100 text-red-800",
      badgeColor: null,
    },
    {
      title: "Segundo bloque",
      value: `${afterCutoffCompliance.toFixed(1)}%`,
      icon: <ExclamationCircleOutlined className="text-2xl" />,
      // color: getComplianceColor(afterCutoffCompliance),
      // subtitle: `Meta: 90%`,
      color: "bg-gray-100 text-gray-600",
      subtitle: null,
      // badge: meetingAfterTarget ? "✓ Cumple" : "✗ No Cumple",
      // badgeColor: meetingAfterTarget
      //   ? "bg-green-100 text-green-800"
      //   : "bg-red-100 text-red-800",
      badge: null,
      badgeColor: null,
    },
    {
      title: "Tiempo promedio",
      value: `${avgVerificationTimeBusinessHours.toFixed(1)} horas`,
      icon: <ClockCircleOutlined className="text-2xl" />,
      color: "bg-green-100 text-green-600",
      subtitle: null,
      badge: null,
      badgeColor: null,
    },
    {
      title: "Requisitos Procesados",
      value: totalProcessed.toLocaleString(),
      icon: <FileTextOutlined className="text-2xl" />,
      color: "bg-purple-100 text-purple-600",
      subtitle: `Efectividad: ${((totalCompliant / totalProcessed) * 100).toFixed(1)}%`,
      badge: userInfo?.team ? `Equipo ${userInfo.team}` : "Usuario",
      badgeColor: "bg-purple-100 text-purple-800",
    },
  ];

  return (
    <div>
      {/* Performance Summary Banner */}
      <div
        className={`rounded-lg p-4 mb-6 ${performance.color.replace("text-", "bg-").replace("-600", "-50")} border-l-4 ${performance.color.replace("bg-", "border-").replace("text-", "").replace("-600", "-500")}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <TrophyOutlined className="text-2xl mr-3" />
            <div>
              <h3 className="text-lg font-semibold">
                Rendimiento General: {performance.level}
              </h3>
              <p className="text-sm opacity-75">
                {overallCompliance >= 85
                  ? "¡Excelente trabajo! Cumpliendo con los estándares SLA."
                  : "Oportunidad de mejora en el cumplimiento de SLA."}
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold">
              {overallCompliance.toFixed(1)}%
            </div>
            <div className="text-sm opacity-75">Cumplimiento Total</div>
          </div>
        </div>
      </div>

      {/* Target Status Alerts */}
      {(!meetingBeforeTarget || !meetingAfterTarget) && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
          <div className="flex items-center">
            <WarningOutlined className="text-yellow-600 text-xl mr-3" />
            <div>
              <h4 className="text-lg font-medium text-yellow-800">
                Objetivos SLA No Cumplidos
              </h4>
              <div className="text-sm text-yellow-700 mt-1">
                {!meetingBeforeTarget && (
                  <p>
                    • Primer Bloque: {beforeCutoffCompliance.toFixed(1)}% (Meta:
                    85%)
                  </p>
                )}
                {!meetingAfterTarget && (
                  <p>
                    • Segundo Bloque: {afterCutoffCompliance.toFixed(1)}% (Meta:
                    90%)
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
        {cardItems.map((item, index) => (
          <div
            key={index}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow duration-200"
          >
            <div className="flex items-start justify-between mb-3">
              <div className={`rounded-md p-3 ${item.color}`}>{item.icon}</div>
              <div
                className={`px-2 py-1 rounded-full text-xs font-medium ${item.badgeColor}`}
              >
                {item.badge}
              </div>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">{item.title}</p>
              <p className="text-2xl font-bold text-gray-900 mb-1">
                {item.value}
              </p>
              <p className="text-xs text-gray-500">{item.subtitle}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Detailed Performance Breakdown */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Análisis Detallado de Rendimiento
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Before Cutoff Analysis */}
          <div className="text-center">
            <div
              className={`inline-flex items-center justify-center w-16 h-16 rounded-full ${getComplianceColor(beforeCutoffCompliance)} mb-3`}
            >
              <ClockCircleOutlined className="text-2xl" />
            </div>
            <h4 className="font-semibold text-gray-900 mb-2">Primer Bloque</h4>
            <div
              className="text-2xl font-bold mb-2"
              style={{
                color: beforeCutoffCompliance >= 85 ? "#10b981" : "#ef4444",
              }}
            >
              {beforeCutoffCompliance.toFixed(1)}%
            </div>
            <div className="text-sm text-gray-600">
              {beforeCutoffCompliance >= 85
                ? "✓ Cumpliendo objetivo (85%)"
                : `Necesita mejorar ${(85 - beforeCutoffCompliance).toFixed(1)}% puntos`}
            </div>
          </div>

          {/* After Cutoff Analysis */}
          <div className="text-center">
            <div
              className={`inline-flex items-center justify-center w-16 h-16 rounded-full ${getComplianceColor(afterCutoffCompliance)} mb-3`}
            >
              <ExclamationCircleOutlined className="text-2xl" />
            </div>
            <h4 className="font-semibold text-gray-900 mb-2">Segundo Bloque</h4>
            <div
              className="text-2xl font-bold mb-2"
              style={{
                color: afterCutoffCompliance >= 90 ? "#10b981" : "#ef4444",
              }}
            >
              {afterCutoffCompliance.toFixed(1)}%
            </div>
            <div className="text-sm text-gray-600">
              {afterCutoffCompliance >= 90
                ? "✓ Cumpliendo objetivo (90%)"
                : `Necesita mejorar ${(90 - afterCutoffCompliance).toFixed(1)}% puntos`}
            </div>
          </div>

          {/* Overall Performance */}
          <div className="text-center">
            <div
              className={`inline-flex items-center justify-center w-16 h-16 rounded-full ${getComplianceColor(overallCompliance)} mb-3`}
            >
              <TrophyOutlined className="text-2xl" />
            </div>
            <h4 className="font-semibold text-gray-900 mb-2">
              Rendimiento General
            </h4>
            <div
              className="text-2xl font-bold mb-2"
              style={{ color: overallCompliance >= 85 ? "#10b981" : "#ef4444" }}
            >
              {overallCompliance.toFixed(1)}%
            </div>
            <div className="text-sm text-gray-600">
              {totalCompliant} de {totalProcessed} requisitos completados a
              tiempo
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SLAUserSummaryCards;
